import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { DialogFooter } from "@/components/ui/dialog";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription, SheetTrigger } from "@/components/ui/sheet";
import { Barbell, BookOpen, VideoCamera, Shield } from "@phosphor-icons/react";
import { useAuth } from "@/contexts/AuthContext";
import { getUnit } from "@/lib/weight";
import WeightInput, { fromCanonicalLb, toCanonicalLb, formatStoredWeight } from "@/components/app/WeightInput";
import UnitInput from "@/components/app/UnitInput";
import MovementAutocomplete from "@/components/blocks/MovementAutocomplete";
import { AnatomyFields, BodyPartFilterPills } from "@/components/app/AnatomyFields";
import { parseScheme, formatScheme } from "@/lib/scheme";
import { formatDuration } from "@/lib/api";
import {
  toCanonicalMeters, fromCanonicalMeters,
  toCanonicalInches, fromCanonicalInches,
} from "@/lib/units";

export default function MovementForm({ initial, onSave, onCancel, readOnlyCore, enableLibrary }) {
  const { user } = useAuth();
  const userUnit = getUnit(user);
  // For weight: keep value in *display unit* and the unit alongside.
  const [m, setM] = useState(() => {
    const base = initial || {
      name: "", sets: 3, reps: 10, weight: null, weight_unit: null,
      time_seconds: null, distance_meters: null, distance_unit: null,
      height_inches: null, height_unit: null,
      calories: null, rpe: null,
      notes: "", video_url: "", form_notes: "", order: 0,
      body_part: null, primary_muscle: null,
    };
    const wUnit = base.weight_unit || userUnit;
    const dUnit = base.distance_unit || "m";
    const hUnit = base.height_unit || "in";
    return {
      ...base,
      weight: fromCanonicalLb(base.weight, wUnit),
      weight_unit: wUnit,
      distance: fromCanonicalMeters(base.distance_meters, dUnit),
      distance_unit: dUnit,
      height: fromCanonicalInches(base.height_inches, hUnit),
      height_unit: hUnit,
      // Per-set scheme strings (hyphen-separated). Persisted on save as arrays.
      reps_scheme_str: formatScheme(base.reps_scheme),
      weight_scheme_str: formatScheme(base.weight_scheme),
      distance_scheme_str: formatScheme(base.distance_scheme),
      calories_scheme_str: formatScheme(base.calories_scheme),
      height_scheme_str: formatScheme(base.height_scheme),
    };
  });
  // Expand the scheme block when any scheme is already populated (editing
  // an existing scheme-style movement) or when the user toggles it open.
  const [showScheme, setShowScheme] = useState(() => {
    const b = initial;
    return !!(b && (b.reps_scheme?.length || b.weight_scheme?.length
      || b.distance_scheme?.length || b.calories_scheme?.length || b.height_scheme?.length));
  });
  const [libOpen, setLibOpen] = useState(false);
  const [lib, setLib] = useState([]);
  const [cfLib, setCfLib] = useState([]);
  const [libSource, setLibSource] = useState("mine"); // "mine" | "crossfit"
  const [libQ, setLibQ] = useState("");
  const [libBodyPart, setLibBodyPart] = useState(null);
  // Local display unit for the Time field — canonical storage stays in
  // `time_seconds`. Defaults to minutes when the pre-filled value is >= 120s.
  const [timeUnit, setTimeUnit] = useState(() =>
    (initial?.time_seconds ?? 0) >= 120 ? "m" : "s"
  );
  useEffect(() => {
    if (libOpen && lib.length === 0) {
      api.get("/exercises").then((r) => setLib(r.data)).catch(() => {});
    }
    if (libOpen && cfLib.length === 0) {
      api.get("/movement-library").then((r) => setCfLib(r.data)).catch(() => {});
    }
  }, [libOpen, lib.length, cfLib.length]);

  const pickExercise = (ex) => {
    // Gym Library picks are just movement templates — pull the name and
    // anatomy classification only. Deliberately blank every prescription
    // field (sets/reps/weight/time/distance/height/calories/rpe) plus
    // notes and form_notes so the coach fills them in fresh for this
    // week's programming. CrossFit Library picks still carry their
    // canonical defaults through — see `pickCrossfit` below.
    setM({
      name: ex.name,
      sets: "",
      reps: null,
      weight: null,
      weight_unit: userUnit,
      time_seconds: null,
      distance: null,
      distance_unit: "m",
      height: null,
      height_unit: "in",
      calories: null,
      rpe: null,
      notes: "",
      video_url: ex.video_url ?? "",
      form_notes: "",
      body_part: ex.body_part ?? null,
      primary_muscle: ex.primary_muscle ?? null,
      order: 0,
    });
    setLibOpen(false);
  };

  const pickCrossfit = (mv) => {
    // Per user preference: any library pick — Gym or CrossFit — carries
    // only the name + anatomy classification through. Prescription fields
    // are blanked so the coach fills them fresh for this session.
    setM({
      name: mv.name,
      sets: "",
      reps: null,
      weight: null,
      weight_unit: userUnit,
      time_seconds: null,
      distance: null,
      distance_unit: "m",
      height: null,
      height_unit: "in",
      calories: null,
      rpe: null,
      notes: "",
      video_url: mv.video_url || "",
      form_notes: "",
      body_part: mv.body_part ?? null,
      primary_muscle: mv.primary_muscle ?? null,
      order: 0,
    });
    setLibOpen(false);
  };

  const libFiltered = lib.filter((e) => {
    if (libBodyPart && (e.body_part || "") !== libBodyPart) return false;
    if (libQ && !e.name.toLowerCase().includes(libQ.toLowerCase())) return false;
    return true;
  });
  const cfFiltered = cfLib.filter((e) => {
    if (libBodyPart && (e.body_part || "") !== libBodyPart) return false;
    if (libQ && !e.name.toLowerCase().includes(libQ.toLowerCase())) return false;
    return true;
  });

  const libSourceItems = libSource === "mine" ? lib : cfLib;
  const libBodyPartCounts = libSourceItems.reduce((acc, e) => {
    const k = e.body_part;
    if (k) acc[k] = (acc[k] || 0) + 1;
    return acc;
  }, {});

  const update = (k, v) => setM((prev) => ({ ...prev, [k]: v }));
  const num = (v) => (v === "" || v === null || v === undefined ? null : Number(v));

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        onSave({
          name: m.name.trim(),
          sets: Number(m.sets) || 1,
          reps: num(m.reps),
          weight: toCanonicalLb(m.weight, m.weight_unit),
          weight_unit: m.weight_unit || "lb",
          time_seconds: num(m.time_seconds),
          distance_meters: toCanonicalMeters(m.distance, m.distance_unit),
          distance_unit: m.distance_unit || "m",
          height_inches: toCanonicalInches(m.height, m.height_unit),
          height_unit: m.height_unit || "in",
          calories: num(m.calories),
          rpe: num(m.rpe),
          notes: m.notes || null,
          video_url: (m.video_url || "").trim() || null,
          form_notes: m.form_notes || null,
          body_part: m.body_part || null,
          primary_muscle: m.primary_muscle || null,
          order: Number(m.order) || 0,
          // Schemes — parse string → array; null when blank or invalid.
          // Weight scheme is sent in canonical lb so the backend stays unit-agnostic.
          reps_scheme: parseScheme(m.reps_scheme_str),
          weight_scheme: (() => {
            const arr = parseScheme(m.weight_scheme_str);
            if (!arr) return null;
            return arr.map((v) => toCanonicalLb(v, m.weight_unit || "lb"));
          })(),
          distance_scheme: (() => {
            const arr = parseScheme(m.distance_scheme_str);
            if (!arr) return null;
            return arr.map((v) => toCanonicalMeters(v, m.distance_unit || "m"));
          })(),
          calories_scheme: parseScheme(m.calories_scheme_str),
          height_scheme: (() => {
            const arr = parseScheme(m.height_scheme_str);
            if (!arr) return null;
            return arr.map((v) => toCanonicalInches(v, m.height_unit || "in"));
          })(),
        });
      }}
      className="flex flex-col max-h-[calc(90vh-80px)] sm:max-h-[calc(85vh-100px)]"
      data-testid="movement-form"
    >
      <div className="space-y-4 overflow-y-auto flex-1 pr-1 -mr-1 pb-3">
      {enableLibrary && !initial && (
        <Sheet open={libOpen} onOpenChange={setLibOpen}>
          <SheetTrigger asChild>
            <Button
              type="button"
              variant="secondary"
              className="w-full rounded-xl bg-sage text-moss hover:bg-sage hover:text-moss-hover font-medium h-11"
              data-testid="pick-from-library-button"
            >
              <BookOpen size={16} className="mr-2" /> Pick from Library
            </Button>
          </SheetTrigger>
          <SheetContent side="right" className="w-full sm:max-w-md overflow-y-auto">
            <SheetHeader className="text-left">
              <SheetTitle className="font-heading">Pick a movement</SheetTitle>
              <SheetDescription>Tap to pre-fill from the gym's shared library or the CrossFit.com library.</SheetDescription>
            </SheetHeader>
            <div className="mt-4 inline-flex bg-muted2 rounded-xl p-1 w-full">
              <button
                type="button"
                onClick={() => setLibSource("mine")}
                className={`flex-1 px-3 py-1.5 rounded-lg text-xs font-semibold uppercase tracking-[0.12em] ${
                  libSource === "mine" ? "bg-surface text-moss" : "text-ink-3"
                }`}
                data-testid="lib-source-mine"
              >
                Gym library
              </button>
              <button
                type="button"
                onClick={() => setLibSource("crossfit")}
                className={`flex-1 px-3 py-1.5 rounded-lg text-xs font-semibold uppercase tracking-[0.12em] ${
                  libSource === "crossfit" ? "bg-surface text-moss" : "text-ink-3"
                }`}
                data-testid="lib-source-crossfit"
              >
                CrossFit · {cfLib.length || 0}
              </button>
            </div>
            <Input
              placeholder="Search…"
              value={libQ}
              onChange={(e) => setLibQ(e.target.value)}
              className="mt-3 h-11 rounded-xl"
              data-testid="library-search"
            />
            <div className="mt-3">
              <BodyPartFilterPills
                value={libBodyPart}
                onChange={setLibBodyPart}
                counts={libBodyPartCounts}
                testIdPrefix="library-picker-body-part"
              />
            </div>
            {libSource === "mine" ? (
              <div className="mt-4 space-y-2">
                {lib.length === 0 && (
                  <div className="text-sm text-ink-3 text-center py-8">
                    The gym library is empty. Save movements from any program to share them with everyone, or switch to the CrossFit tab above.
                  </div>
                )}
                {libFiltered.map((ex) => (
                  <button
                    key={ex.id}
                    type="button"
                    onClick={() => pickExercise(ex)}
                    className="w-full text-left p-3 rounded-xl border border-line hover:bg-sage hover:border-sage transition-colors"
                    data-testid={`library-item-${ex.id}`}
                  >
                    <div className="flex items-center gap-2">
                      <Barbell size={16} className="text-moss" />
                      <span className="font-medium">{ex.name}</span>
                    </div>
                    <div className="mt-1 text-xs font-data text-ink-3">
                      {[ex.sets && `${ex.sets}×${ex.reps ?? "?"}`,
                        ex.weight && `@ ${formatStoredWeight(ex.weight, ex.weight_unit || userUnit)}`,
                        ex.time_seconds && formatDuration(ex.time_seconds),
                        ex.distance_meters && `${ex.distance_meters}m`,
                        ex.calories && `${ex.calories} cal`,
                      ].filter(Boolean).join(" · ")}
                    </div>
                  </button>
                ))}
              </div>
            ) : (
              <div className="mt-4 space-y-2">
                {cfFiltered.length === 0 && (
                  <div className="text-sm text-ink-3 text-center py-8">
                    No CrossFit movements match.
                  </div>
                )}
                {cfFiltered.map((mv) => (
                  <button
                    key={mv.name}
                    type="button"
                    onClick={() => pickCrossfit(mv)}
                    className="w-full text-left p-3 rounded-xl border border-line hover:bg-sage hover:border-sage transition-colors"
                    data-testid={`crossfit-pick-${mv.name.toLowerCase().replace(/\s+/g, "-")}`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-2 min-w-0">
                        <Barbell size={16} className="text-moss shrink-0" />
                        <span className="font-medium truncate">{mv.name}</span>
                      </div>
                      <span className="text-[10px] uppercase tracking-[0.15em] text-ink-3 shrink-0">{mv.category}</span>
                    </div>
                    <div className="mt-1 text-xs font-data text-ink-3">
                      {[mv.default_reps && `${mv.default_reps} reps`,
                        mv.default_weight && `@ ${mv.default_weight} lb`,
                        mv.default_time_seconds && formatDuration(mv.default_time_seconds),
                        mv.default_distance_meters && `${mv.default_distance_meters}m`,
                        mv.default_calories && `${mv.default_calories} cal`,
                      ].filter(Boolean).join(" · ")}
                    </div>
                  </button>
                ))}
              </div>
            )}
          </SheetContent>
        </Sheet>
      )}
      <div>
        <Label className="hs-label">Movement</Label>
        <div className="mt-2">
          <MovementAutocomplete
            value={m.name}
            onChange={(v) => update("name", v)}
            onPickFull={(picked) => {
              // Pre-fill the whole form from the picked library entry — but
              // keep any values the coach already typed in (don't clobber).
              // Any autocomplete pick — Gym Library or CrossFit — only
              // carries the name + anatomy classification. Prescription
              // fields stay blank so the coach fills them for this session.
              setM((prev) => ({
                ...prev,
                name: picked.name,
                sets: prev.sets || "",
                reps: prev.reps,
                weight: prev.weight,
                weight_unit: prev.weight_unit || userUnit,
                time_seconds: prev.time_seconds,
                distance: prev.distance,
                distance_unit: prev.distance_unit || "m",
                height: prev.height,
                height_unit: prev.height_unit || "in",
                calories: prev.calories,
                rpe: prev.rpe,
                notes: prev.notes,
                video_url: prev.video_url || picked.video_url || "",
                form_notes: prev.form_notes,
                body_part: prev.body_part || picked.body_part || null,
                primary_muscle: prev.primary_muscle || picked.primary_muscle || null,
              }));
            }}
            disabled={readOnlyCore}
            placeholder="Goblet Squat, Turkish Get-Up…"
            required
          />
        </div>
      </div>
      <div className="grid grid-cols-3 gap-3">
        <div>
          <Label className="hs-label">Sets</Label>
          <Input type="number" min="1" value={m.sets} disabled={readOnlyCore} onChange={(e) => update("sets", e.target.value)} className="mt-2 h-11 rounded-xl font-data" data-testid="movement-sets" />
        </div>
        <div>
          <Label className="hs-label">Reps</Label>
          <Input type="number" min="0" value={m.reps ?? ""} onChange={(e) => update("reps", e.target.value)} className="mt-2 h-11 rounded-xl font-data" data-testid="movement-reps" />
        </div>
        <WeightInput
          value={m.weight}
          unit={m.weight_unit}
          onChange={(v, u) => setM({ ...m, weight: v, weight_unit: u })}
          testId="movement-weight"
        />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <div className="flex items-center justify-between">
            <Label className="hs-label">Time ({timeUnit === "m" ? "min" : "s"})</Label>
            <div className="inline-flex rounded-full border border-line overflow-hidden text-[10px] font-bold uppercase tracking-[0.14em]">
              <button
                type="button"
                onClick={() => setTimeUnit("s")}
                className={`px-2 py-0.5 ${timeUnit === "s" ? "bg-moss text-white" : "bg-white text-ink-3"}`}
                data-testid="movement-time-unit-s"
              >S</button>
              <button
                type="button"
                onClick={() => setTimeUnit("m")}
                className={`px-2 py-0.5 ${timeUnit === "m" ? "bg-moss text-white" : "bg-white text-ink-3"}`}
                data-testid="movement-time-unit-m"
              >Min</button>
            </div>
          </div>
          <Input
            type="number"
            step={timeUnit === "m" ? "0.5" : "1"}
            min="0"
            max={timeUnit === "m" ? "120" : "7200"}
            value={
              m.time_seconds == null || m.time_seconds === ""
                ? ""
                : timeUnit === "m"
                  ? Number((m.time_seconds / 60).toFixed(2))
                  : m.time_seconds
            }
            onChange={(e) => {
              const raw = e.target.value;
              if (raw === "") { update("time_seconds", ""); return; }
              const v = Number(raw);
              if (Number.isNaN(v)) return;
              // Clamp to 120 minutes (7200 s) regardless of unit.
              const secs = Math.min(7200, Math.max(0, timeUnit === "m" ? Math.round(v * 60) : Math.round(v)));
              update("time_seconds", secs);
            }}
            className="mt-2 h-11 rounded-xl font-data"
            data-testid="movement-time"
          />
        </div>
        <div>
          <Label className="hs-label">Calories</Label>
          <Input type="number" step="1" min="0" value={m.calories ?? ""} onChange={(e) => update("calories", e.target.value)} className="mt-2 h-11 rounded-xl font-data" data-testid="movement-calories" />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <UnitInput
          kind="distance"
          value={m.distance}
          unit={m.distance_unit}
          onChange={(v, u) => setM({ ...m, distance: v, distance_unit: u })}
          testId="movement-distance"
        />
        <UnitInput
          kind="height"
          value={m.height}
          unit={m.height_unit}
          onChange={(v, u) => setM({ ...m, height: v, height_unit: u })}
          testId="movement-height"
        />
      </div>
      <div>
        <Label className="hs-label">RPE</Label>
        <Input type="number" step="0.5" min="0" max="10" value={m.rpe ?? ""} onChange={(e) => update("rpe", e.target.value)} className="mt-2 h-11 rounded-xl font-data" data-testid="movement-rpe" />
      </div>
      <AnatomyFields
        bodyPart={m.body_part}
        primaryMuscle={m.primary_muscle}
        onChange={({ body_part, primary_muscle }) => setM({ ...m, body_part, primary_muscle })}
      />
      <div>
        <Label className="hs-label">Notes</Label>
        <Textarea value={m.notes ?? ""} onChange={(e) => update("notes", e.target.value)} className="mt-2 rounded-xl" rows={3} placeholder="Tempo, cues, equipment…" data-testid="movement-notes" />
      </div>

      {/* Per-set scheme block — collapsed by default, expand when this is a */}
      {/* "21-15-9"-style workout or a ramping strength set (10-8-6-4-2 etc). */}
      <div className="rounded-xl border border-line">
        <button
          type="button"
          onClick={() => setShowScheme((s) => !s)}
          className="w-full px-4 py-3 flex items-center justify-between font-medium text-sm hover:bg-muted2/60 rounded-xl"
          data-testid="movement-scheme-toggle"
        >
          <span className="inline-flex items-center gap-2">
            <span className="hs-label text-[10px] text-terracotta">Per-set scheme</span>
            <span className="text-ink-3 text-xs">21-15-9 · 10-8-6-4-2 · ramps…</span>
          </span>
          <span className="text-ink-3 text-lg leading-none">{showScheme ? "−" : "+"}</span>
        </button>
        {showScheme && (
          <div className="px-4 pb-4 pt-1 space-y-3" data-testid="movement-scheme-fields">
            <p className="text-xs text-ink-3">
              Enter the per-set values separated by dashes. When the athlete logs set N, that set&apos;s value auto-fills.
              Leave blank to use the single value above for every set.
            </p>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="hs-label">Reps scheme</Label>
                <Input value={m.reps_scheme_str ?? ""} onChange={(e) => update("reps_scheme_str", e.target.value)} placeholder="21-15-9" className="mt-2 h-11 rounded-xl font-data" data-testid="movement-reps-scheme" />
              </div>
              <div>
                <Label className="hs-label">Weight scheme ({m.weight_unit || "lb"})</Label>
                <Input value={m.weight_scheme_str ?? ""} onChange={(e) => update("weight_scheme_str", e.target.value)} placeholder="95-115-135-155" className="mt-2 h-11 rounded-xl font-data" data-testid="movement-weight-scheme" />
              </div>
              <div>
                <Label className="hs-label">Distance scheme ({m.distance_unit || "m"})</Label>
                <Input value={m.distance_scheme_str ?? ""} onChange={(e) => update("distance_scheme_str", e.target.value)} placeholder="200-400-800" className="mt-2 h-11 rounded-xl font-data" data-testid="movement-distance-scheme" />
              </div>
              <div>
                <Label className="hs-label">Calories scheme</Label>
                <Input value={m.calories_scheme_str ?? ""} onChange={(e) => update("calories_scheme_str", e.target.value)} placeholder="21-15-9" className="mt-2 h-11 rounded-xl font-data" data-testid="movement-calories-scheme" />
              </div>
              <div className="col-span-2">
                <Label className="hs-label">Height scheme ({m.height_unit || "in"})</Label>
                <Input value={m.height_scheme_str ?? ""} onChange={(e) => update("height_scheme_str", e.target.value)} placeholder="20-24-30" className="mt-2 h-11 rounded-xl font-data" data-testid="movement-height-scheme" />
              </div>
            </div>
          </div>
        )}
      </div>
      <div>
        <Label className="hs-label flex items-center gap-1.5"><VideoCamera size={12} /> Demo video URL</Label>
        <Input
          type="url"
          value={m.video_url ?? ""}
          onChange={(e) => update("video_url", e.target.value)}
          disabled={readOnlyCore}
          className="mt-2 h-11 rounded-xl font-data text-sm"
          placeholder="https://youtube.com/…"
          data-testid="movement-video-url"
        />
      </div>
      <div>
        <Label className="hs-label flex items-center gap-1.5"><Shield size={12} /> Form-check notes (safety cues)</Label>
        <Textarea
          value={m.form_notes ?? ""}
          onChange={(e) => update("form_notes", e.target.value)}
          disabled={readOnlyCore}
          className="mt-2 rounded-xl"
          rows={3}
          placeholder="Knees track over toes. Neutral spine. Breathe on the way up…"
          data-testid="movement-form-notes"
        />
      </div>
      </div>
      <DialogFooter className="shrink-0 border-t border-line pt-4 mt-2 bg-surface flex-row gap-2 sm:flex-row">
        <Button type="button" variant="ghost" onClick={onCancel} className="rounded-xl flex-1 sm:flex-initial h-12">Cancel</Button>
        <Button type="submit" className="rounded-xl flex-1 sm:flex-initial h-12 bg-moss hover:bg-moss-hover text-white hover:text-white shadow-lift" data-testid="save-movement-button">Save</Button>
      </DialogFooter>
    </form>
  );
}
